import { useState, useEffect } from 'react';
import { 
  PlusCircle, Trash2, ChevronDown, ChevronRight, Send, 
  Lightbulb, Eye, EyeOff, Edit, AlertTriangle, X,
  Sparkles, Zap, Shield, Check, Settings, Wand2,
  Search, Grid, List, Tag, Database, Bot, MessageSquare
} from 'lucide-react';
import Modal from '../ui/Modal';
import MCPService from '../../services/MCPService';
import ToolCard from './ToolCard';
import JsonEditor from './JsonEditor';
import ParameterBuilder from './ParameterBuilder';

const ModernToolIcon = ({ iconSrc, altText, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-28 h-28'
  };
  
  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      <div className={`relative ${sizeClasses[size]} rounded-2xl bg-gradient-to-br from-indigo-100 to-green-100 p-0.5 shadow-lg`}>
        <div className="absolute inset-0 bg-white rounded-2xl overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white to-white opacity-50"></div>
          <img
            src={iconSrc}
            alt={altText}
            className={`${sizeClasses[size]} object-fit drop-shadow-md`}
          />
        </div>
      </div>
    </div>
  );
};

const DependencyAlertModal = ({ alert, onClose, onAddTool }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 mr-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{alert.alert_title}</h3>
            <p className="mt-1 text-sm text-gray-600">{alert.alert_description}</p>
          </div>
          <button 
            onClick={onClose} 
            className="flex-shrink-0 ml-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            onClick={onClose}
          >
            Entendi
          </button>
          
          {alert.show_add_button && alert.add_tool_id && (
            <button
              type="button"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg flex items-center gap-2"
              onClick={() => {
                onClose();
                onAddTool(alert.add_tool_id);
              }}
            >
              <PlusCircle className="w-4 h-4" />
              Adicionar habilidade necessária
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentTools = ({ tools, onToolsChange, agentId }) => {
  const [availableInternalTools, setAvailableInternalTools] = useState([]);
  const [showToolSelector, setShowToolSelector] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});
  const [alertInfo, setAlertInfo] = useState(null);

  useEffect(() => {
     MCPService.getAvailableTools(undefined, tools, localStorage.getItem('authToken')).then((internalTools) => {
        setAvailableInternalTools(internalTools);
      })
  }, [tools]);

    // Componente Workspace de Ferramentas
  const ToolWorkspace = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // grid ou list
    const [categories, setCategories] = useState([
      { id: 'all', name: 'Todas', icon: <Sparkles className="w-4 h-4" />, count: availableInternalTools.length },
      { id: 'communication', name: 'Comunicação', icon: <MessageSquare className="w-4 h-4" />, count: 0 },
      { id: 'integration', name: 'Integrações', icon: <Zap className="w-4 h-4" />, count: 0 },
      { id: 'ai', name: 'IA & Automação', icon: <Bot className="w-4 h-4" />, count: 0 },
      { id: 'data', name: 'Dados', icon: <Database className="w-4 h-4" />, count: 0 },
    ]);
    
    // Contar ferramentas por categoria
    useEffect(() => {
      const counts = { communication: 0, integration: 0, ai: 0, data: 0 };
      availableInternalTools.forEach(tool => {
        if (tool.tags?.includes('api')) counts.integration++;
        else if (tool.tags?.includes('ia') || tool.tags?.includes('ai')) counts.ai++;
        else if (tool.tags?.includes('data')) counts.data++;
      });
      
      categories.forEach(cat => {
        if (cat.id !== 'all') {
          cat.count = counts[cat.id] || 0;
        }
      });
      setCategories([...categories])
    }, [availableInternalTools]);
    
    // Filtrar ferramentas
    const filteredTools = availableInternalTools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tool.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      if (selectedCategory === 'all') return matchesSearch;
      if (selectedCategory === 'integration') return matchesSearch && tool.tags?.includes('integração');
      if (selectedCategory === 'ai') return matchesSearch && (tool.tags?.includes('ia') || tool.tags?.includes('ai'));
      if (selectedCategory === 'data') return matchesSearch && tool.tags?.includes('dados');
      
      return matchesSearch;
    });
    
    return (
      <div className="h-full -mx-8 -my-6">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-green-800 via-green-600 to-green-600 px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                <Sparkles className="w-8 h-8" />
                Central de Habilidades
              </h2>
              <p className="text-white/80 text-lg mt-1">
                Escolha superpoderes para turbinar seu funcionário digital
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowToolSelector(false)}
                className="p-2 hover:bg-indigo-400 rounded-2xl transition-all"
                aria-label="Fechar"
              >
                <X className="w-10 h-10 text-white" />
              </button>
            </div>
          </div>
          
          {/* Barra de pesquisa */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Pesquisar habilidades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg bg-white/95 backdrop-blur rounded-2xl border-2 border-transparent focus:border-white/50 focus:outline-none focus:ring-4 focus:ring-white/20 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-lg transition-all"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
        
        {/* Container principal */}
        <div className="flex h-[calc(100%-180px)]">
          {/* Sidebar de categorias */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Categorias
            </h3>
            <div className="space-y-1">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-indigo-500 to-indigo-500 text-white shadow-lg'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <span className="font-medium">{category.name}</span>
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    selectedCategory === category.id
                      ? 'bg-white/20'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Estatísticas */}
            <div className="mt-8 p-4 bg-gradient-to-br from-indigo-50 to-indigo-50 rounded-2xl border border-indigo-100">
              <h4 className="font-semibold text-gray-800 mb-2">💡 Você sabia?</h4>
              <p className="text-sm text-gray-600">
                Funcionários com mais habilidades resolvem 3x mais problemas automaticamente!
              </p>
            </div>
          </div>
          
          {/* Grid de ferramentas */}
          <div className="flex-1 p-6 overflow-y-auto">
            {filteredTools.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nenhuma habilidade encontrada
                </h3>
                <p className="text-gray-600">
                  Tente buscar por outros termos ou explore outras categorias
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {filteredTools.length} habilidades disponíveis
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Tag className="w-4 h-4" />
                    <span>Clique para adicionar</span>
                  </div>
                </div>
                
                <div className={
                  viewMode === 'grid' 
                    ? "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4"
                    : "space-y-3"
                }>
                  {filteredTools.map((tool) => (
                    viewMode === 'grid' ? (
                      <ToolCard 
                        key={tool.id}
                        tool={tool}
                        onClick={handleSelectTool}
                        disabled={!tool.available}
                        alert={tool.alert}
                      />
                    ) : (
                      <ToolListItem
                        key={tool.id}
                        tool={tool}
                        onClick={handleSelectTool}
                        disabled={!tool.available}
                        alert={tool.alert}
                      />
                    )
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Componente para visualização em lista
  const ToolListItem = ({ tool, onClick, disabled, alert }) => {
    return (
      <button
        onClick={() => !disabled && onClick(tool)}
        disabled={disabled}
        className={`w-full p-4 bg-white rounded-2xl border-2 transition-all flex items-center gap-4 ${
          disabled
            ? 'border-gray-200 opacity-60 cursor-not-allowed'
            : 'border-gray-200 hover:border-indigo-300 hover:shadow-lg hover:scale-[1.01] cursor-pointer'
        }`}
      >
        <ModernToolIcon 
          iconSrc={tool.provider_icon} 
          altText={tool.provider_title} 
          size="sm"
        />
        <div className="flex-1 text-left">
          <h4 className="font-semibold text-gray-900">{tool.name}</h4>
          <p className="text-sm text-gray-600 line-clamp-1">{tool.description}</p>
        </div>
        {tool.tags && tool.tags.length > 0 && (
          <div className="flex gap-2">
            {tool.tags.slice(0, 2).map((tag, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </button>
    );
  };


  const toggleToolExpansion = (index) => {
    setExpandedTools(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSelectTool = (tool) => {
    const dependencyCheck = MCPService.checkToolDependencies(tool, tools);
    
    if (!dependencyCheck.canAdd) {
      setAlertInfo(dependencyCheck.alert);
      return;
    }
    
    setSelectedTool(tool);
    
    const initialValues = {};
    tool.form.forEach(field => {
      initialValues[field.value] = '';
    });
    
    setFormValues(initialValues);
    setShowToolSelector(false);
    setShowAddForm(true);
  };

  const handleAddToolById = (toolId) => {
    const tool = MCPService.getToolById(toolId);
    if (tool) {
      handleSelectTool(tool);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleJsonChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswords(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const handleAddOrUpdateTool = (e) => {
    e.preventDefault();
    
    const missingFields = selectedTool.form
      .filter(field => field.required && !formValues[field.value])
      .map(field => field.label);
    
    if (missingFields.length > 0) {
      alert(`Por favor, preencha os seguintes campos obrigatórios: ${missingFields.join(', ')}`);
      return;
    }

    let validationError = false;
    selectedTool.form.forEach(field => {
      if (field.type === 'json' && formValues[field.value]) {
        try {
          JSON.parse(formValues[field.value]);
        } catch (e) {
          alert(`O campo ${field.label} contém JSON inválido: ${e.message}`);
          validationError = true;
        }
      }
    });

    if (validationError) return;

    const processedFormValues = { ...formValues };
    selectedTool.form.forEach(field => {
      if (field.type === 'json' && processedFormValues[field.value]) {
        try {
          processedFormValues[field.value] = JSON.parse(processedFormValues[field.value]);
        } catch (e) {
          // Mantenha como string se ocorrer algum erro
        }
      }
    });

    const configuredTool = MCPService.convertToolForPlatform(selectedTool, processedFormValues);
    
    if (configuredTool.tool_schema && configuredTool.tool_schema) {
      const schema = configuredTool.tool_schema;
      
      if (schema && schema.name === "{{form_name}}") {
        const formattedName = processedFormValues.name
          .toLowerCase()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
        
        configuredTool.tool_schema.name = formattedName;
      }
      
      if (schema && schema.description === "{{form_description}}") {
        configuredTool.tool_schema.description = processedFormValues.description || '';
      }
      
      if (schema && schema.parameters === "{{form_build_parameters}}") {
        try {
          let parametersSchema = JSON.parse(processedFormValues.parameters_schema || '{}');
          
          if (!parametersSchema.type) {
            parametersSchema.type = 'object';
          }
          
          if (parametersSchema.properties) {
            Object.keys(parametersSchema.properties).forEach(propName => {
              const prop = parametersSchema.properties[propName];
              
              if (prop.type === 'array' && prop.items && prop.items.type === 'object') {
                if (!prop.items.additionalProperties) {
                  prop.items.additionalProperties = false;
                }
                
                if (prop.items.properties) {
                  const requiredProps = [];
                  Object.keys(prop.items.properties).forEach(itemPropName => {
                    const itemProp = prop.items.properties[itemPropName];
                    if (itemProp._required === true) {
                      requiredProps.push(itemPropName);
                      delete itemProp._required;
                    }
                  });
                  
                  if (requiredProps.length > 0) {
                    prop.items.required = requiredProps;
                  }
                }
              }
              
              if (prop.type === 'object' && prop.properties) {
                if (!prop.additionalProperties) {
                  prop.additionalProperties = false;
                }
              }
            });
          }
          
          configuredTool.tool_schema.parameters = parametersSchema;
        } catch (error) {
          console.error("Erro ao processar schema de parâmetros:", error);
          alert("Erro ao processar o schema de parâmetros. Verifique o formato.");
          return;
        }
      }
    }
    
    let updatedTools;
    if (editingIndex !== null) {
      updatedTools = tools.map((tool, index) =>
        index === editingIndex ? configuredTool : tool
      );
    } else {
      updatedTools = [...tools, configuredTool];
    }
    
    onToolsChange(updatedTools, false);
    resetForm();
  };

  const handleEditTool = (index) => {
    const toolToEdit = tools[index];
    const originalTool = MCPService.getToolById(toolToEdit.id);
    
    if (originalTool) {
      setSelectedTool(originalTool);
      
      const editFormValues = { ...toolToEdit.form_values };
      
      originalTool.form.forEach(field => {
        if (field.type === 'json' && typeof editFormValues[field.value] === 'object') {
          editFormValues[field.value] = JSON.stringify(editFormValues[field.value], null, 2);
        }
      });
      
      setFormValues(editFormValues || {});
      setEditingIndex(index);
      setShowAddForm(true);
    } else {
      alert("Erro: Não foi possível encontrar a definição desta habilidade.");
    }
  };

  const handleRemoveTool = (indexToRemove) => {
    if (window.confirm('Tem certeza que deseja remover esta habilidade? O funcionário perderá esta capacidade.')) {
      const updatedTools = tools.filter((_, index) => index !== indexToRemove);
      onToolsChange(updatedTools, false);
      
      if (editingIndex === indexToRemove) {
        resetForm();
      }
    }
  };

  const resetForm = () => {
    setSelectedTool(null);
    setFormValues({});
    setShowAddForm(false);
    setShowToolSelector(false);
    setEditingIndex(null);
  };

  const renderToolIcon = (tool) => {


    return <ModernToolIcon 
        iconSrc={tool.provider_icon} 
        altText={tool.provider_title} 
        size="md"
        className="mr-4"
    />
  };

  return (
    <div className="space-y-6">
      {/* Modal de Alerta para Dependências */}
      {alertInfo && (
        <DependencyAlertModal 
          alert={alertInfo} 
          onClose={() => setAlertInfo(null)}
          onAddTool={handleAddToolById}
        />
      )}

      <Modal 
        isOpen={showToolSelector} 
        onClose={() => setShowToolSelector(false)}
        size="xl"
        title=""
      >
        <ToolWorkspace />
      </Modal>

      {/* Modal para Configuração de Ferramenta */}
      <Modal 
        isOpen={showAddForm && selectedTool} 
        onClose={resetForm}
        size={'lg'}
        title={
          editingIndex !== null 
              ? `Editar ${selectedTool?.name}` 
              : `Configurar ${selectedTool?.name}`
        }
      >
        {selectedTool && (
          <>
            <form onSubmit={handleAddOrUpdateTool} className="space-y-6">
                {/* Header do formulário */}
                <div className="flex items-center mb-6">
                  {selectedTool.provider_icon ? (
                    <ModernToolIcon 
                      iconSrc={selectedTool.provider_icon} 
                      altText={selectedTool.provider_title} 
                      size="md"
                      className="mr-4"
                    />
                  ) : (
                    <div className="mr-4">{renderToolIcon(selectedTool)}</div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800">
                      {selectedTool.name}
                    </h4>
                    {selectedTool.provider_title && (
                      <span className="text-sm text-gray-600">
                        Fornecido por {selectedTool.provider_title}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-4 border border-indigo-100">
                  <p className="text-sm text-gray-700 flex items-start gap-2">
                    <Settings className="w-4 h-4 text-green-600 mt-0.5" />
                    {selectedTool.description}
                  </p>
                </div>

                {/* Campos do formulário */}
                      <div className="space-y-5">
                        {selectedTool.form.map((field, index) => (
                          <div key={index}>
                            <label htmlFor={`field-${field.value}`} className="block text-sm font-semibold text-gray-700 mb-2">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            
                            <div className="relative">
                              {field.type === 'select' ? (
                                <select
                                  id={`field-${field.value}`}
                                  name={field.value}
                                  value={formValues[field.value] || ''}
                                  onChange={handleInputChange}
                                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-base transition-all"
                                  required={field.required}
                                >
                                  <option value="">Selecione uma opção</option>
                                  {field.options.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : field.type === 'json' ? (
                                <div className="relative">
                                  <JsonEditor 
                                    value={formValues[field.value] || ''}
                                    onChange={(value) => handleJsonChange(field.value, value)}
                                    isPassword={field.is_password}
                                    showPassword={showPasswords[field.value]}
                                  />
                                  
                                  {field.is_password && (
                                    <button
                                      type="button"
                                      className="absolute top-3 right-3 p-2 bg-white hover:bg-gray-50 rounded-xl shadow-sm border transition-all"
                                      onClick={() => togglePasswordVisibility(field.value)}
                                    >
                                      {showPasswords[field.value] ? 
                                        <EyeOff className="h-4 w-4 text-gray-500" /> : 
                                        <Eye className="h-4 w-4 text-gray-500" />
                                      }
                                    </button>
                                  )}
                                </div>
                              ) : field.type === 'build_parameters' ? (
                                <div className="relative">
                                  <ParameterBuilder
                                    value={formValues[field.value] || ''}
                                    onChange={(value) => handleJsonChange(field.value, value)}
                                  />
                                </div>
                              ) : (
                                <>
                                  <input
                                    type={field.is_password && !showPasswords[field.value] ? "password" : "text"}
                                    id={`field-${field.value}`}
                                    name={field.value}
                                    value={formValues[field.value] || ''}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-base transition-all"
                                    placeholder={`Digite ${field.label.toLowerCase()}`}
                                    required={field.required}
                                  />
                                  
                                  {field.is_password && (
                                    <button
                                      type="button"
                                      className="absolute inset-y-0 right-0 px-4 flex items-center"
                                      onClick={() => togglePasswordVisibility(field.value)}
                                    >
                                      {showPasswords[field.value] ? 
                                        <EyeOff className="h-5 h-5 text-gray-500" /> : 
                                        <Eye className="h-5 h-5 text-gray-500" />
                                      }
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {field.hint && (
                              <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
                                <Lightbulb className="w-3 h-3 mt-0.5" />
                                {field.hint}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                {/* Botões do Formulário */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-2xl font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>{editingIndex !== null ? 'Atualizar' : 'Adicionar'} Habilidade</span>
                  </button>
                </div>
            </form>
          </>
        )}
      </Modal>

      {/* Botão para Adicionar Habilidade */}
      {!showAddForm && !showToolSelector && (
        <button
          onClick={() => setShowToolSelector(true)}
          className="w-full flex items-center justify-center text-green-600 p-6 border-3 border-dashed border-indigo-300 rounded-3xl hover:bg-indigo-50 hover:border-indigo-400 transition-all group"
        >
          <div className="flex flex-col items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-green-100 rounded-2xl group-hover:scale-110 transition-transform">
              <Wand2 className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-lg font-semibold">Clique aqui para Adicionar Habilidade Especial</span>
            <span className="text-sm text-gray-600">Dê superpoderes ao seu funcionário</span>
          </div>
        </button>
      )}

      {/* Lista de Habilidades Adicionadas */}
      <div className="space-y-4">
        {tools.length === 0 && !showAddForm && !showToolSelector && (
          <div className="text-center py-12 px-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border-2 border-amber-100">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl flex items-center justify-center">
              <Lightbulb className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhuma habilidade especial ainda</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Adicione recursos como conversão de texto em áudio, geração de imagens, análise de dados e muito mais para tornar seu funcionário ainda mais poderoso!
            </p>
          </div>
        )}
        
        {tools.map((tool, index) => (
          <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
            <button
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
              onClick={() => toggleToolExpansion(index)}
            >
              <div className='flex items-center'>
                {expandedTools[index] ? 
                  <ChevronDown className="w-5 h-5 mr-3 text-indigo-500" /> : 
                  <ChevronRight className="w-5 h-5 mr-3 text-gray-400" />
                }
                {renderToolIcon(tool)}
                <div>
                  <span className="font-semibold text-gray-800 text-lg">{tool.name}</span>
                  {tool.provider_title && (
                    <span className="ml-3 px-3 py-1 bg-gradient-to-r from-indigo-100 to-green-100 text-green-700 text-xs rounded-full font-medium">
                      {tool.provider_title}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); handleEditTool(index); }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                  aria-label={`Editar ${tool.name}`}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleRemoveTool(index); }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  aria-label={`Remover ${tool.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </button>

            {/* Detalhes Expansíveis */}
            {expandedTools[index] && (
              <div className="px-6 pb-6 pt-2 border-t bg-gradient-to-b from-gray-50 to-white animate-fade-in">
                <p className="text-gray-600 mb-4">{tool.description}</p>
                
                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                    <h5 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      Configurações Ativas
                    </h5>
                    <div className="space-y-2">
                      {tool.form_values && Object.keys(tool.form_values).length > 0 ? (
                        <>
                          {(() => {
                            const originalTool = availableInternalTools.find(t => t.id === tool.id);
                            if (!originalTool) return null;
                            
                            return Object.entries(tool.form_values).map(([key, value], i) => {
                              const fieldConfig = originalTool.form.find(f => f.value === key);
                              const label = fieldConfig ? fieldConfig.label : key;
                              const isPassword = fieldConfig ? fieldConfig.is_password : false;
                              const isJson = fieldConfig ? fieldConfig.type === 'json' : false;
                              
                              return (
                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                  <span className="text-sm font-medium text-gray-700">{label}</span>
                                  {isPassword || key.includes('key') || key.includes('password') || key.includes('secret') || key.includes('credentials') ? (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                                      <Check className="w-3 h-3" />
                                      Configurado com segurança
                                    </span>
                                  ) : isJson || typeof value === 'object' ? (
                                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                      Configuração avançada ativa
                                    </span>
                                  ) : (
                                    <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">
                                      {String(value)}
                                    </span>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </>
                      ) : (
                        <p className="text-gray-500 italic text-center py-2">Usando configurações padrão</p>
                      )}
                    </div>
                  </div>
                
                {tool.tags && tool.tags.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-medium text-gray-700 mb-2">Capacidades:</h5>
                    <div className="flex flex-wrap gap-2">
                      {tool.tags.map((tag, i) => (
                        <span key={i} className="bg-gradient-to-r from-indigo-100 to-green-100 text-green-700 text-sm px-3 py-1 rounded-full font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Estilos para animações */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AgentTools;